import { useMemo, useState } from 'react'
import { perfilParaEstado, volumeTanqueM3, type PerfilTanque } from '../../../dominio/caminhaoTanque/perfil'
import {
  PRODUTOS,
  aparenciaRotulo,
  lerNumeroRisco,
  produtoPorId,
  type IdProduto,
} from '../../../dominio/produtos'
import { VENTO_PADRAO, pontoCardeal } from '../../../dominio/vento'
import { CaminhaoTanque3D, chaveParte, type ParteCaminhao } from '../../../visualizacao3d/caminhaoTanque/CaminhaoTanque3D'
import type { VolumeEnquadramento } from '../../../visualizacao3d/camera/comandosCamera'
import { Biruta3D } from '../../../visualizacao3d/cenario/Biruta3D'
import { Pista3D } from '../../../visualizacao3d/cenario/Pista3D'
import { Terreno3D } from '../../../visualizacao3d/cenario/Terreno3D'
import {
  IconeAvaliacao,
  IconeCena,
  IconeEnquadrarSelecao,
  IconeInfo,
  IconeModuloCaminhao,
  IconeOrbitar,
  IconeRestaurarCamera,
} from '../../icones'
import type { ConteudoInspetor } from '../../modulos/tipos'
import { CascaOficina, type ContextoCasca } from '../../workspace/CascaOficina'
import { FichaProduto } from '../comum/FichaProduto'
import { SeletorProduto } from '../comum/SeletorProduto'
import { LeitorPainel } from './LeitorPainel'

const VOLUME: VolumeEnquadramento = { comprimento: 22, largura: 10, altura: 4.5 }
/** O caminhão para na faixa da direita, sentido +Z. */
const POSICAO_CAMINHAO: [number, number, number] = [1.75, 0, 0]

interface Desafio {
  idSorteado: IdProduto
  resposta: IdProduto | null
}

function sortear(diferenteDe?: IdProduto): IdProduto {
  const candidatos = PRODUTOS.filter((p) => p.id !== diferenteDe)
  return candidatos[Math.floor(Math.random() * candidatos.length)]!.id
}

const DESCRICAO_TIPO: Record<PerfilTanque['aberturas'][number]['tipo'], string> = {
  valvulaFaseLiquida: 'Válvula — fase líquida',
  valvulaFaseVapor: 'Válvula — fase vapor',
  valvulaFundo: 'Válvula de fundo',
  valvulaAlivio: 'Dispositivo de alívio',
  bocaVisita: 'Boca de visita',
}

/**
 * Oficina de identificação: o caminhão-tanque mostra painel de segurança e
 * rótulos do produto escolhido; o aluno lê o painel, consulta a ficha e as
 * distâncias iniciais do GRE. No desafio, o produto é sorteado e escondido —
 * só o painel e o rótulo contam o que há no tanque.
 */
export function OficinaIdentificacao({ contexto }: { contexto: ContextoCasca }) {
  const [idEscolhido, setIdEscolhido] = useState<IdProduto>('glp')
  const [desafio, setDesafio] = useState<Desafio | null>(null)
  const [parte, setParte] = useState<ParteCaminhao | null>(null)

  const idProduto = desafio?.idSorteado ?? idEscolhido
  const produto = produtoPorId(idProduto)
  const perfil = useMemo(() => perfilParaEstado(produto.estadoTransporte), [produto.estadoTransporte])
  const classes = useMemo(() => [produto.classe.valor, ...produto.riscosSubsidiarios], [produto])
  const oculto = desafio != null && desafio.resposta == null
  const leitura = lerNumeroRisco(produto.numeroRisco.valor)

  const iniciarDesafio = () => {
    setDesafio({ idSorteado: sortear(desafio?.idSorteado), resposta: null })
    setParte({ tipo: 'painel' })
    contexto.camera.executar('enquadrarSelecao', 'painel')
  }

  const ativarCameraLivre = () => {
    setParte(null)
    contexto.camera.executar('liberarCamera')
  }

  const inspetor = ((): ConteudoInspetor | null => {
    if (!parte) return null
    const aoFechar = () => setParte(null)
    switch (parte.tipo) {
      case 'painel':
        return {
          titulo: 'Painel de segurança',
          subtitulo: 'Placa laranja: número de risco em cima, número ONU embaixo',
          propriedades: [
            { rotulo: 'Número de risco', valor: `${produto.numeroRisco.valor} — ${leitura.resumo}` },
            { rotulo: 'Número ONU', valor: String(produto.onu) },
            { rotulo: 'Produto', valor: oculto ? 'descubra pelo painel e pelo rótulo' : produto.nome },
          ],
          aoFechar,
        }
      case 'rotulo': {
        const aparencia = aparenciaRotulo(parte.classe)
        return {
          titulo: `Rótulo de risco — classe ${parte.classe}`,
          subtitulo: aparencia.nome,
          propriedades: [
            { rotulo: 'Papel', valor: parte.classe === produto.classe.valor ? 'risco principal' : 'risco subsidiário' },
          ],
          aoFechar,
        }
      }
      case 'abertura': {
        const abertura = perfil.tanque.aberturas.find((a) => a.id === parte.id)
        if (!abertura) return null
        return {
          titulo: abertura.nome,
          subtitulo: DESCRICAO_TIPO[abertura.tipo],
          propriedades: [
            { rotulo: 'Captação', valor: abertura.anguloRad === 0 ? 'no topo do tanque' : 'no fundo do tanque' },
            { rotulo: 'Para que serve', valor: abertura.descricao },
          ],
          aoFechar,
        }
      }
      case 'tanque':
        return {
          titulo: perfil.tanque.nome,
          subtitulo: perfil.tanque.secao === 'circular' ? 'Seção circular — resiste à pressão interna' : 'Seção elíptica — pressão atmosférica, centro de gravidade baixo',
          propriedades: [
            { rotulo: 'Volume interno', valor: `${volumeTanqueM3(perfil.tanque).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} m³` },
            { rotulo: 'Compartimentos', valor: String(perfil.tanque.compartimentos) },
            { rotulo: 'Dimensões', valor: `${perfil.tanque.comprimentoM} m × ${perfil.tanque.larguraM} m × ${perfil.tanque.alturaM} m (perfil didático)` },
          ],
          aoFechar,
        }
      case 'cavalo':
        return {
          titulo: 'Cavalo mecânico',
          subtitulo: 'Também leva painel de segurança na frente',
          propriedades: [{ rotulo: 'Dica', valor: 'Aproxime-se a favor do vento e de longe: leia o painel com binóculo.' }],
          aoFechar,
        }
    }
  })()

  const grupos = [
    {
      id: 'produto',
      rotulo: 'Produto',
      icone: <IconeModuloCaminhao />,
      desabilitado: desafio != null,
      resumo: desafio ? 'desafio em andamento' : produto.nome,
      conteudo: () => (
        <SeletorProduto
          idSelecionado={idEscolhido}
          aoSelecionar={(id) => {
            setIdEscolhido(id)
            setParte(null)
          }}
        />
      ),
    },
    {
      id: 'painel',
      rotulo: 'Ler painel',
      icone: <IconeInfo />,
      conteudo: () => <LeitorPainel />,
    },
    {
      id: 'desafio',
      rotulo: 'Desafio',
      icone: <IconeAvaliacao />,
      ativa: desafio != null,
      conteudo: () => (
        <div className="grupo-controle">
          <span className="titulo-controle">Que produto está no tanque?</span>
          {!desafio ? (
            <>
              <p className="nota-controle">Um produto é sorteado e o nome fica escondido. Leia o painel e o rótulo no 3D e responda.</p>
              <button type="button" className="btn-acao-cad btn-acao-cad--primario" onClick={iniciarDesafio}>
                Sortear produto
              </button>
            </>
          ) : (
            <>
              <div className="linha-botoes">
                <button
                  type="button"
                  className={`btn-acao-cad ${parte?.tipo === 'painel' ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
                  onClick={() => {
                    setParte({ tipo: 'painel' })
                    contexto.camera.executar('enquadrarSelecao', 'painel')
                  }}
                  title="Aproxima a câmera da placa de sinalização (como um binóculo)"
                >
                  <IconeEnquadrarSelecao tamanho={14} /> Focar placa
                </button>
                <button
                  type="button"
                  className={`btn-acao-cad ${!parte ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
                  onClick={ativarCameraLivre}
                  title="Permite mexer e orbitar livremente pelo caminhão e pelo vento"
                >
                  <IconeOrbitar tamanho={14} /> Câmera livre
                </button>
              </div>
              <div className="seletor-produto" role="group" aria-label="Resposta do desafio">
                {PRODUTOS.map((p) => {
                  const respondido = desafio.resposta != null
                  const certo = p.id === desafio.idSorteado
                  const escolhido = p.id === desafio.resposta
                  const classe = respondido && certo ? 'btn-acao-cad--sucesso' : respondido && escolhido ? 'btn-acao-cad--perigo' : 'btn-acao-cad--secundario'
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={`btn-acao-cad ${classe}`}
                      disabled={respondido}
                      onClick={() => setDesafio({ ...desafio, resposta: p.id })}
                    >
                      {p.nome}
                    </button>
                  )
                })}
              </div>
              {desafio.resposta && (
                <p className={`feedback-colocacao${desafio.resposta === desafio.idSorteado ? ' correta' : ''}`} role="status">
                  <strong>{desafio.resposta === desafio.idSorteado ? 'Correto.' : `Era ${produto.nome}.`}</strong>
                  Painel {produto.numeroRisco.valor}/{produto.onu}: {leitura.resumo}; guia GRE {produto.guiaGre.valor}.
                </p>
              )}
              <div className="linha-botoes">
                <button type="button" className="btn-acao-cad btn-acao-cad--primario" onClick={iniciarDesafio}>
                  Novo sorteio
                </button>
                <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={() => setDesafio(null)}>
                  Encerrar desafio
                </button>
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      id: 'cena',
      rotulo: 'Cena',
      icone: <IconeCena />,
      conteudo: () => (
        <div className="grupo-controle">
          <span className="titulo-controle">Partes do caminhão</span>
          <p className="nota-controle">Toque no painel laranja, nos rótulos, nas válvulas ou no tanque para ver o que são.</p>
          <div className="seletor-produto">
            {[{ tipo: 'painel' } as const, { tipo: 'rotulo', classe: produto.classe.valor } as const, { tipo: 'tanque' } as const].map((p) => (
              <button
                key={chaveParte(p)}
                type="button"
                className={`btn-acao-cad ${parte && chaveParte(parte) === chaveParte(p) ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
                onClick={() => {
                  setParte(p)
                  contexto.camera.executar('enquadrarSelecao', chaveParte(p))
                }}
              >
                {p.tipo === 'painel' ? 'Painel de segurança' : p.tipo === 'rotulo' ? 'Rótulo de risco' : 'Tanque'}
              </button>
            ))}
            <button
              type="button"
              className={`btn-acao-cad ${!parte ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
              onClick={ativarCameraLivre}
              title="Libera a seleção e restaura a órbita livre ao redor do caminhão"
            >
              <IconeOrbitar tamanho={14} /> Câmera livre
            </button>
          </div>
        </div>
      ),
    },
  ]

  return (
    <CascaOficina
      contexto={contexto}
      oficina={{
        volume: VOLUME,
        distanciaMaxima: 120,
        distanciaMinima: 0.8,
        cameraLivre: true,
        corFundo: '#26333f',
        neblinaM: [150, 600],
        idSelecionado: parte ? chaveParte(parte) : null,
        nomeSelecionado: inspetor?.titulo ?? null,
        aoClicarVazio: () => setParte(null),
        ferramentas: {
          grupos,
          chips: [
            { id: 'produto', rotulo: 'Produto', valor: oculto ? 'oculto (desafio)' : produto.nome, tom: oculto ? 'alerta' : 'info' },
            { id: 'painel', rotulo: 'Painel', valor: `${produto.numeroRisco.valor} / ${produto.onu}` },
            ...(oculto ? [] : [{ id: 'guia', rotulo: 'Guia GRE', valor: String(produto.guiaGre.valor) }]),
          ],
          acoesRapidas: (
            <div className="mesa-acoes">
              <button
                type="button"
                className={`btn-acao-cad ${parte?.tipo === 'painel' ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
                onClick={() => {
                  setParte({ tipo: 'painel' })
                  contexto.camera.executar('enquadrarSelecao', 'painel')
                }}
                title="Aproxima a câmera da placa de sinalização mais próxima (como um binóculo)"
              >
                <IconeEnquadrarSelecao tamanho={14} /> Focar placa
              </button>
              <button
                type="button"
                className={`btn-acao-cad ${!parte ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
                onClick={ativarCameraLivre}
                title="Desprende o foco da placa e permite navegar e orbitar livremente ao redor de todo o caminhão"
              >
                <IconeOrbitar tamanho={14} /> Câmera livre
              </button>
            </div>
          ),
          inspetor,
        },
        operacao: oculto ? (
          <div className="grupo-controle">
            <span className="titulo-controle">Desafio</span>
            <p className="nota-controle">A ficha aparece depois da resposta. Use o painel e o rótulo, e o grupo "Ler painel".</p>
          </div>
        ) : (
          <FichaProduto produto={produto} />
        ),
        itensEstado: [
          { id: 'vento', rotulo: 'Vento', valor: `de ${pontoCardeal(VENTO_PADRAO.direcaoGraus)}, ${VENTO_PADRAO.velocidadeMS} m/s` },
        ],
        cena: (
          <>
            <Terreno3D ladoM={1200} />
            <Pista3D comprimentoM={600} />
            <Biruta3D posicao={[-9, -6]} direcaoVentoGraus={VENTO_PADRAO.direcaoGraus} velocidadeMS={VENTO_PADRAO.velocidadeMS} />
            <group position={POSICAO_CAMINHAO}>
              <CaminhaoTanque3D
                perfil={perfil}
                numeroRisco={produto.numeroRisco.valor}
                onu={produto.onu}
                classes={classes}
                chaveSelecionada={parte ? chaveParte(parte) : null}
                aoSelecionar={setParte}
              />
            </group>
          </>
        ),
      }}
    />
  )
}
